import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, till } from "@/lib/api";
import { Badge, Button, Modal, cx, useToast } from "./ui";
import { downloadCsv } from "./Insights";

type Key = "name" | "sku" | "barcode" | "category" | "brand" | "unit" | "price" | "cost" | "wholesalePrice" | "wholesaleMin" | "packs" | "stock" | "reorderAt";
type Result = { row: number; status: "new" | "update" | "same" | "error"; name: string; message: string };
type Check = { results: Result[]; counts: Record<Result["status"], number>; ids?: string[] };

/** Template headings, and the other ways people write them. */
const COLUMNS: [Key, string, string[]][] = [
  ["name", "Name", ["product", "product name", "item", "item name", "description"]],
  ["sku", "SKU", ["code", "item code", "product code"]],
  ["barcode", "Barcode", ["ean", "upc", "bar code"]],
  ["category", "Category", ["group", "department"]],
  ["brand", "Brand", ["make", "manufacturer"]],
  ["unit", "Unit", ["uom", "unit of measure", "sold by"]],
  ["price", "Price", ["selling price", "retail price", "retail", "unit price", "price (ghs)"]],
  ["cost", "Cost", ["cost price", "buying price", "purchase price", "cost (ghs)"]],
  ["wholesalePrice", "Wholesale price", ["wholesale"]],
  ["wholesaleMin", "Wholesale min qty", ["wholesale min", "wholesale qty", "min wholesale"]],
  ["packs", "Packs", ["pack", "pack sizes"]],
  ["stock", "Stock", ["qty", "quantity", "on hand", "stock on hand", "opening stock", "in stock"]],
  ["reorderAt", "Reorder at", ["reorder", "reorder level", "min stock", "minimum stock"]],
];
const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9()]+/g, " ").trim();
const keyOf = (heading: string): Key | null => {
  const h = norm(heading);
  return COLUMNS.find(([k, label, alts]) => norm(label) === h || norm(k) === h || alts.includes(h))?.[0] ?? null;
};

/** A CSV as rows of text: commas or semicolons, quoted cells, Excel's BOM. */
function parseCsv(text: string): string[][] {
  const t = text.replace(/^﻿/, "");
  const first = t.split(/\r?\n/, 1)[0] ?? "";
  const sep = (first.match(/;/g)?.length ?? 0) > (first.match(/,/g)?.length ?? 0) ? ";" : ",";
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < t.length; i++) {
    const ch = t[i]!;
    if (quoted) {
      if (ch === '"' && t[i + 1] === '"') (cell += '"'), i++;
      else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === sep) row.push(cell), (cell = "");
    else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && t[i + 1] === "\n") i++;
      row.push(cell), rows.push(row), (row = []), (cell = "");
    } else cell += ch;
  }
  if (cell || row.length) row.push(cell), rows.push(row);
  return rows;
}

async function readFile(file: File): Promise<string[][]> {
  if (/\.xlsx$/i.test(file.name)) {
    const { readSheet } = await import("read-excel-file/browser");
    const data = await readSheet(file);
    return data.map((r) => r.map((v) => (v == null ? "" : v instanceof Date ? v.toISOString().slice(0, 10) : String(v))));
  }
  if (/\.xls$/i.test(file.name)) throw new Error("That's an old Excel file (.xls). In Excel choose File › Save as › Excel Workbook (.xlsx), then try again");
  return parseCsv(await file.text());
}

export function downloadTemplate() {
  downloadCsv("products-template", [
    COLUMNS.map((c) => c[1]),
    ["Dangote cement 50kg", "CEM-DAN-50", "6151100000011", "Cement", "Dangote", "bag", "80", "67.50", "76", "20", "pallet=40", "400", "100"],
    ["Iron rod 12mm", "ROD-12", "", "Steel", "", "length", "40", "30", "37", "50", "bundle=10", "120", "60"],
    ["Wood screws 2in (box of 100)", "SCR-2IN", "", "Fasteners", "", "box", "35", "22", "", "", "", "45", "10"],
  ]);
}

/** Products from a CSV or Excel file: read it, check it with the server (nothing saved), then import. */
export function ImportProducts({ onClose, onImported }: { onClose: () => void; /** The products added or updated, for their labels. */ onImported?: (ids: string[]) => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [file, setFile] = useState("");
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [cols, setCols] = useState<{ heading: string; key: Key | null }[]>([]);
  const [check, setCheck] = useState<Check | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [show, setShow] = useState<"all" | Result["status"]>("all");

  const pick = async (f: File | undefined) => {
    if (!f) return;
    setErr("");
    setCheck(null);
    setBusy(true);
    try {
      const grid = (await readFile(f)).filter((r) => r.some((c) => c.trim()));
      if (grid.length < 2) throw new Error("The file needs a row of headings and at least one product");
      const head = grid[0]!.map((h) => ({ heading: h.trim(), key: keyOf(h) }));
      if (!head.some((h) => h.key === "name") && !head.some((h) => h.key === "sku")) throw new Error('No "Name" or "SKU" column found. Use the template headings');
      const body = grid.slice(1).map((r) => Object.fromEntries(head.flatMap((h, i) => (h.key ? [[h.key, (r[i] ?? "").trim()]] : []))));
      if (body.length > 3000) throw new Error(`That's ${body.length} rows. Import up to 3,000 at a time`);
      setFile(f.name);
      setCols(head);
      setRows(body);
      const r = await api<Check>(till("/admin/items/import"), { body: { rows: body, commit: false } });
      setCheck(r);
      setShow(r.counts.error ? "error" : "all");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't read that file");
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await api<Check>(till("/admin/items/import"), { body: { rows, commit: true } });
      if (r.counts.error) {
        setCheck(r);
        setShow("error");
        return;
      }
      toast(`${r.counts.new} added, ${r.counts.update} updated`);
      void qc.invalidateQueries({ queryKey: ["admin-items"] });
      onClose();
      if (r.ids?.length) onImported?.(r.ids);
    } catch (e) {
      toast(e instanceof Error ? e.message : "The import didn't save", "err");
    } finally {
      setBusy(false);
    }
  };

  const list = (check?.results ?? []).filter((r) => (show === "all" ? r.status !== "same" || r.message !== "Empty row, skipped" : r.status === show));
  const tone = { new: "brand", update: "blue", same: "slate", error: "red" } as const;
  const label: Record<Result["status"], string> = { new: "New", update: "Update", same: "No change", error: "Problem" };
  const ready = !!check && !check.counts.error && check.counts.new + check.counts.update > 0;
  return (
    <Modal
      title="Import products"
      sub="From Excel (.xlsx) or a CSV file. Matched by SKU, then barcode: matches are updated, the rest are added."
      onClose={onClose}
      xl
      footer={
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <span className="flex-1" />
          {check?.counts.error ? <span className="text-sm font-semibold text-red-800">Fix the problems in the file and choose it again</span> : null}
          <Button tone="primary" disabled={!ready || busy} onClick={() => void save()}>
            {busy && check ? "Importing…" : check ? `Import ${check.counts.new + check.counts.update} product${check.counts.new + check.counts.update === 1 ? "" : "s"}` : "Import"}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-2 border border-slate-200 p-4">
            <b className="text-sm">1 · Get the template</b>
            <span className="text-[13px] text-slate-600">Opens in Excel. Fill one product per row; money in cedis. Leave a cell empty to keep what's there. Packs like "bundle=10; pallet=40". No barcode? Leave it empty: one is made, and the labels open after the import.</span>
            <Button onClick={downloadTemplate} className="self-start">
              Download template
            </Button>
          </div>
          <label className={cx("flex cursor-pointer flex-col gap-2 border-2 border-dashed p-4", busy ? "border-slate-200 opacity-60" : "border-slate-300 hover:border-brand")}>
            <b className="text-sm">2 · Choose your file</b>
            <span className="text-[13px] text-slate-600">{file ? `${file} · ${rows.length} row${rows.length === 1 ? "" : "s"}` : "Excel (.xlsx) or CSV. Nothing is saved until you press Import."}</span>
            <input type="file" accept=".xlsx,.csv,text/csv" disabled={busy} onChange={(e) => void pick(e.target.files?.[0]).finally(() => (e.target.value = ""))} className="text-sm" />
          </label>
        </div>
        {err ? <p className="bg-red-50 px-3 py-2 text-sm font-semibold text-red-800">{err}</p> : null}
        {busy && !check ? <p className="text-sm text-slate-500">Checking the file…</p> : null}
        {cols.length ? (
          <div className="flex flex-wrap gap-1.5 text-xs">
            {cols.map((c, i) => (
              <span key={i} className={cx("px-2 py-1", c.key ? "bg-slate-100" : "bg-amber-50 text-amber-900 line-through")} title={c.key ? undefined : "Not a column we know; ignored"}>
                {c.heading || "(no heading)"}
              </span>
            ))}
          </div>
        ) : null}
        {check ? (
          <>
            <div role="tablist" className="flex flex-wrap gap-2">
              {(["all", "new", "update", "same", "error"] as const).map((k) => (
                <button key={k} type="button" role="tab" aria-selected={show === k} onClick={() => setShow(k)} className={cx("h-9 border px-3 text-[13px] font-semibold", show === k ? "border-brand bg-brand-tint" : "border-slate-200 bg-white")}>
                  {k === "all" ? "All" : label[k]} · {k === "all" ? check.results.length : check.counts[k]}
                </button>
              ))}
            </div>
            <div className="max-h-[46vh] overflow-auto border border-slate-200">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-2 font-semibold">Row</th>
                    <th className="px-3 py-2 font-semibold">Product</th>
                    <th className="px-3 py-2 font-semibold" />
                    <th className="px-3 py-2 font-semibold">What happens</th>
                  </tr>
                </thead>
                <tbody>
                  {list.slice(0, 500).map((r) => (
                    <tr key={r.row} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-mono text-slate-500">{r.row}</td>
                      <td className="px-3 py-2 font-semibold">{r.name}</td>
                      <td className="px-3 py-2">
                        <Badge tone={tone[r.status]}>{label[r.status]}</Badge>
                      </td>
                      <td className={cx("px-3 py-2", r.status === "error" && "font-semibold text-red-800")}>{r.message}</td>
                    </tr>
                  ))}
                  {!list.length ? (
                    <tr>
                      <td colSpan={4} className="px-3 py-4 text-slate-500">
                        Nothing here.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            {list.length > 500 ? <span className="text-xs text-slate-500">Showing the first 500.</span> : null}
          </>
        ) : null}
      </div>
    </Modal>
  );
}
